import { useState, useEffect, useContext } from "react";
import { Heart, GraduationCap, ClipboardCheck } from "lucide-react";
import { cn } from "../../lib/utils";
import { UserModulesContext } from "../../../../components/UserModuleContext/index";
import TabHeroCard from "./TabHeroCard";
import BoardChemistrySection from "./BoardChemistrySection";
import BoardGovernancePulse from "./BoardGovernancePulse";
import BoardTrainingDevelopment from "./BoardTrainingDevelopment";
import ChairBriefBanner from "./ChairBriefBanner";
import { fetchChairBrief } from "../../services/businessDashApi";

const GOVERNANCE_NAV_KEY = "az_governance_nav";

const GovernanceIcon = () => (
  <svg
    className="w-6 h-10 text-white"
    viewBox="0 0 16 28"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden
  >
    <rect x="1" y="1" width="14" height="26" rx="2" ry="2" />
    <path d="M8 5 L8 8" />
    <circle cx="8" cy="11" r="3" />
    <path d="M5 14 L8 17 L11 14" />
    <path d="M4 20 L12 20" />
    <path d="M5 23 L11 23" />
  </svg>
);

const canViewChairSummary = (role) => {
  if (role == null || role === "") return false;
  const r = String(role).toLowerCase().replace(/\s+/g, "-");
  return (
    r === "chairperson" ||
    r === "president" ||
    r === "secretary" ||
    r === "admin" ||
    r === "administrator"
  );
};

/**
 * @param {{ initialSection?: 'chemistry'|'pulse'|'training', autoStartChemistry?: boolean, autoStartPulse?: boolean }} props
 */
const GovernanceTab = ({
  initialSection = "chemistry",
  autoStartChemistry = false,
  autoStartPulse = false,
}) => {
  const { boardMemberRole } = useContext(UserModulesContext);
  const [activeSection, setActiveSection] = useState(initialSection);
  const [shouldAutoStartChemistry, setShouldAutoStartChemistry] = useState(autoStartChemistry);
  const [shouldAutoStartPulse, setShouldAutoStartPulse] = useState(autoStartPulse);
  const [chairBriefPayload, setChairBriefPayload] = useState(null);
  const [chairBriefLoading, setChairBriefLoading] = useState(false);

  const isChairOrAdmin = canViewChairSummary(boardMemberRole);

  useEffect(() => {
    if (!isChairOrAdmin) {
      setChairBriefPayload(null);
      return;
    }
    let alive = true;
    (async () => {
      setChairBriefLoading(true);
      try {
        const raw = await fetchChairBrief();
        if (!alive) return;
        setChairBriefPayload(raw);
      } catch {
        if (alive) setChairBriefPayload(null);
      } finally {
        if (alive) setChairBriefLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [isChairOrAdmin]);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(GOVERNANCE_NAV_KEY);
      if (!raw) return;
      const nav = JSON.parse(raw);
      sessionStorage.removeItem(GOVERNANCE_NAV_KEY);
      if (nav.section === "chemistry" || nav.section === "pulse" || nav.section === "training") {
        setActiveSection(nav.section);
      }
      if (nav.autoStartChemistry) setShouldAutoStartChemistry(true);
      if (nav.autoStartPulse) setShouldAutoStartPulse(true);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (shouldAutoStartChemistry) {
      const t = setTimeout(() => setShouldAutoStartChemistry(false), 500);
      return () => clearTimeout(t);
    }
  }, [shouldAutoStartChemistry]);

  useEffect(() => {
    if (shouldAutoStartPulse) {
      const t = setTimeout(() => setShouldAutoStartPulse(false), 500);
      return () => clearTimeout(t);
    }
  }, [shouldAutoStartPulse]);

  const sections = [
    {
      id: "chemistry",
      label: "Board Chemistry",
      icon: Heart,
      subtitle: "How the board works together",
    },
    {
      id: "pulse",
      label: "Board Governance Pulse",
      icon: ClipboardCheck,
      subtitle: "How the board governs and decides",
    },
    {
      id: "training",
      label: "Board Training & Development",
      icon: GraduationCap,
      subtitle: "How the board builds capability",
    },
  ];

  return (
    <div className="space-y-8 w-full max-w-6xl mx-auto">
      <TabHeroCard
        title="GOVERNANCE"
        icon={<GovernanceIcon />}
        headline="Why does governance structure matter?"
        description="A documented governance structure is essential for audits, certifications (ISO, SOC 2), and regulatory reporting (CSRD, ESG). Systematic board evaluations create verifiable audit trails that demonstrate oversight quality to stakeholders and regulators."
        subdescription="Track board chemistry, governance pulse, and development activities to maintain compliance-ready documentation."
      />

      {isChairOrAdmin && (
        <ChairBriefBanner briefRaw={chairBriefPayload} loading={chairBriefLoading} />
      )}

      <div className="flex justify-center">
        <div className="inline-flex bg-primary/10 rounded-xl p-2 gap-2 border border-primary/20 shadow-sm flex-wrap justify-center">
          {sections.map((section) => {
            const Icon = section.icon;
            const isActive = activeSection === section.id;
            return (
              <button
                key={section.id}
                type="button"
                onClick={() => setActiveSection(section.id)}
                className={cn(
                  "flex flex-col items-center gap-1 px-4 sm:px-6 py-3 rounded-lg text-sm transition-all duration-200 min-w-[140px]",
                  isActive
                    ? "bg-[#09366b] text-white shadow-md"
                    : "bg-background/80 text-foreground hover:bg-background hover:shadow-sm border border-border/50"
                )}
              >
                <div className="flex items-center gap-2">
                  <Icon
                    className={cn(
                      "w-5 h-5",
                      isActive ? "text-white" : "text-primary"
                    )}
                  />
                  <span className="font-semibold text-left">{section.label}</span>
                </div>
                <span
                  className={cn(
                    "text-xs text-center",
                    isActive ? "text-white/80" : "text-foreground/50"
                  )}
                >
                  {section.subtitle}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="min-h-[400px]">
        {activeSection === "chemistry" && (
          <BoardChemistrySection
            isChair={isChairOrAdmin}
            autoStartAssessment={shouldAutoStartChemistry}
          />
        )}
        {activeSection === "pulse" && (
          <BoardGovernancePulse
            isChair={isChairOrAdmin}
            autoStartAssessment={shouldAutoStartPulse}
          />
        )}
        {activeSection === "training" && <BoardTrainingDevelopment />}
      </div>
    </div>
  );
};

export default GovernanceTab;
